import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ACCOUNT_TYPE_LABELS } from '../../constants/accountTypes';

// ヘボン式→訓令式変換（kanaフィールドが訓令式で保存されているため）
const normalizeRomaji = (str) => str
    .replace(/TSU/g, 'TU')
    .replace(/SHO/g, 'SYO')
    .replace(/SHI/g, 'SI')
    .replace(/SHA/g, 'SYA')
    .replace(/SHU/g, 'SYU')
    .replace(/NN/g, 'N'); // 「ん」入力のnn→n正規化

export const AccountSelect = ({ accounts, value, onChange, placeholder = '科目を選択（入力で検索）', lineType = null, halfWidth = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    const selectedAccount = accounts.find(a => a.id === value);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredAccounts = useMemo(() => {
        if (!searchTerm) return accounts;
        const term = searchTerm.toUpperCase();
        const normalizedTerm = normalizeRomaji(term);
        return accounts.filter(acc =>
            acc.name.includes(searchTerm) ||
            (acc.kana && acc.kana.toUpperCase().includes(normalizedTerm)) ||
            acc.code.includes(searchTerm)
        );
    }, [accounts, searchTerm]);

    const { primaryGroups, secondaryGroups } = useMemo(() => {
        const typeOrder = ['asset', 'liability', 'equity', 'revenue', 'expense'];
        const primaryGroups = {};
        const secondaryGroups = {};

        const preferredHome = lineType;

        typeOrder.forEach(type => {
            const accs = filteredAccounts.filter(a => a.type === type);
            if (accs.length > 0) {
                const sorted = accs.sort((a, b) => a.code.localeCompare(b.code));
                if (preferredHome) {
                    const primary = sorted.filter(a => a.home === preferredHome);
                    const secondary = sorted.filter(a => a.home !== preferredHome);
                    if (primary.length > 0) primaryGroups[type] = primary;
                    if (secondary.length > 0) secondaryGroups[type] = secondary;
                } else {
                    primaryGroups[type] = sorted;
                }
            }
        });
        return { primaryGroups, secondaryGroups };
    }, [filteredAccounts, lineType]);

    const handleSelect = (acc) => {
        onChange(acc.id);
        setIsOpen(false);
        setSearchTerm('');
    };

    // 半角モード: IMEに渡る前にkeydownで直接拾う
    const handleKeyDown = (e) => {
        if (!halfWidth) return;
        if (e.key === 'Backspace') {
            e.preventDefault();
            setSearchTerm(prev => prev.slice(0, -1));
            if (!isOpen) setIsOpen(true);
        } else if (/^[a-zA-Z0-9\-]$/.test(e.key)) {
            e.preventDefault();
            setSearchTerm(prev => prev + e.key);
            if (!isOpen) setIsOpen(true);
        }
    };

    // 全角モード: 通常のonChange
    const handleInputChange = (e) => {
        if (halfWidth) return;
        setSearchTerm(e.target.value);
        if (!isOpen) setIsOpen(true);
    };

    const handleInputFocus = () => {
        setIsOpen(true);
    };

    return (
        <div className="account-select-container" ref={containerRef}>
            <input
                ref={inputRef}
                type="text"
                className="account-select-input"
                value={isOpen ? searchTerm : (selectedAccount ? selectedAccount.name : '')}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={handleInputFocus}
                placeholder={placeholder}
                title={selectedAccount?.description || ''}
                style={lineType && selectedAccount && selectedAccount.home !== lineType
                    ? {backgroundColor: '#fff5f5', borderColor: '#e74c3c'}
                    : {}
                }
            />
            {isOpen && (
                <div className="account-select-dropdown">
                    {Object.entries(primaryGroups).map(([type, accs]) => (
                        <div key={type} className="account-select-group">
                            <div className="account-select-group-label">
                                【{ACCOUNT_TYPE_LABELS[type]}】
                            </div>
                            {accs.map(acc => (
                                <div
                                    key={acc.id}
                                    className={`account-select-item ${acc.id === value ? 'selected' : ''}`}
                                    onClick={() => handleSelect(acc)}
                                    title={acc.description || ''}
                                >
                                    <span className="account-name">{acc.name}</span>
                                    {acc.kana && <span className="account-kana">{acc.kana}</span>}
                                </div>
                            ))}
                        </div>
                    ))}
                    {Object.keys(secondaryGroups).length > 0 && (
                        <div style={{backgroundColor: '#fff5f5', borderTop: '2px solid #e74c3c', marginTop: '8px'}}>
                            <div style={{padding: '4px 12px', fontSize: '11px', color: '#c0392b', fontWeight: 'bold'}}>
                                {lineType === 'debit' ? '▼ 通常は貸方（逆仕訳用）' : '▼ 通常は借方（逆仕訳用）'}
                            </div>
                            {Object.entries(secondaryGroups).map(([type, accs]) => (
                                <div key={`secondary-${type}`} className="account-select-group" style={{backgroundColor: '#fff5f5'}}>
                                    <div className="account-select-group-label" style={{color: '#c0392b'}}>
                                        【{ACCOUNT_TYPE_LABELS[type]}】
                                    </div>
                                    {accs.map(acc => (
                                        <div
                                            key={acc.id}
                                            className={`account-select-item ${acc.id === value ? 'selected' : ''}`}
                                            onClick={() => handleSelect(acc)}
                                            title={acc.description || ''}
                                            style={{color: '#666', backgroundColor: '#fff5f5'}}
                                        >
                                            <span className="account-name">{acc.name}</span>
                                            {acc.kana && <span className="account-kana">{acc.kana}</span>}
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}
                    {Object.keys(primaryGroups).length === 0 && Object.keys(secondaryGroups).length === 0 && (
                        <div style={{padding: '12px', color: '#888', textAlign: 'center'}}>
                            該当する科目がありません
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AccountSelect;
